import React, { useState } from 'react';
import { X, Trash2, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

const REQUIRED_PHRASE = 'DELETE ALL DATA';

/**
 * Factory-reset modal — wipes EVERY collection in the current
 * environment's DB and re-seeds the minimum bootable state (default
 * admin + default tenant + Chart of Accounts). Requires the operator
 * to type ``DELETE ALL DATA`` verbatim, plus a double window.confirm.
 */
const FactoryResetModal = ({ open, onClose, onSuccess }) => {
  const [phrase, setPhrase] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const reset = () => { setPhrase(''); setResult(null); };
  const handleClose = () => { if (running) return; reset(); onClose?.(); };

  const runReset = async () => {
    if (phrase.trim() !== REQUIRED_PHRASE) {
      toast.error(`يجب كتابة "${REQUIRED_PHRASE}" حرفياً للتأكيد`);
      return;
    }
    if (!window.confirm(
      `⛔ تحذير أخير\n\n`
      + `أنت على وشك مسح كل البيانات في هذه البيئة:\n`
      + `${window.location.host}\n\n`
      + `⚠️ هذا الإجراء لا يمكن التراجع عنه.\n`
      + `⚠️ سيتم مسح جميع الفواتير، القيود، الحسابات، المستخدمين، إلخ.\n`
      + `⚠️ ستُعاد فقط الشركة الافتراضية + مسؤول افتراضي.\n\n`
      + `هل أنت متأكد تماماً؟`
    )) return;

    setRunning(true);
    setResult(null);
    try {
      const r = await axiosInstance.post('/data-migration/factory-reset', {
        confirmation_phrase: phrase.trim(),
      }, { timeout: 120000 });
      setResult(r.data);
      toast.success(
        `✅ تم المسح · ${r.data.collections_dropped} مجموعة حُذفت`,
        { duration: 8000 });
      onSuccess?.(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'فشل المسح';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg),
                    { duration: 12000 });
    } finally {
      setRunning(false);
    }
  };

  const phraseValid = phrase.trim() === REQUIRED_PHRASE;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
         data-testid="factory-reset-overlay">
      <div className="bg-white rounded-lg shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto"
           data-testid="factory-reset-modal">
        <div className="flex items-center justify-between p-5 border-b bg-gradient-to-r from-red-50 to-orange-50">
          <div className="flex items-center gap-3">
            <Trash2 className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="text-lg font-bold text-red-800">
                🗑️ مسح كامل + بدء من جديد
              </h3>
              <p className="text-xs text-red-600 mt-0.5">
                Factory Reset (destructive · irreversible)
              </p>
            </div>
          </div>
          <button onClick={handleClose} disabled={running}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  data-testid="factory-reset-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!result && (
            <>
              <div className="p-4 bg-red-50 border-2 border-red-400 rounded-lg text-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="text-red-900 space-y-2">
                    <div className="font-bold text-base">
                      سيتم مسح جميع بيانات هذه البيئة:
                      <br /><code className="text-xs">{typeof window !== 'undefined' ? window.location.host : ''}</code>
                    </div>
                    <ul className="text-xs space-y-1 mr-4 list-disc">
                      <li>جميع الفواتير، القيود، الحسابات</li>
                      <li>جميع المستخدمين والشركات (باستثناء واحدة افتراضية)</li>
                      <li>جميع المشاريع، العملاء، الموردون</li>
                      <li>جميع المدفوعات، إقرارات VAT، البنوك</li>
                      <li>جميع المرفقات والتقارير</li>
                    </ul>
                    <div className="text-xs font-bold pt-2 border-t border-red-200">
                      لا يمكن التراجع عن هذا الإجراء.
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-sm">
                <div className="text-yellow-800">
                  <strong>سيتم بذر تلقائياً بعد المسح:</strong>
                  <ul className="text-xs mr-4 mt-1 list-disc space-y-0.5">
                    <li>مستخدم مسؤول افتراضي: <code>admin@test.com</code></li>
                    <li>كلمة المرور: <code>Dhr@2026Secure!</code></li>
                    <li>شركة افتراضية: <code>cmp_001</code> (DHR-001)</li>
                    <li>دليل الحسابات الافتراضي (52 حساب)</li>
                  </ul>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  اكتب <code className="bg-gray-100 px-1.5 py-0.5 rounded text-red-600 font-bold">{REQUIRED_PHRASE}</code> للتأكيد *
                </label>
                <input
                  type="text"
                  value={phrase}
                  onChange={e => setPhrase(e.target.value)}
                  placeholder={REQUIRED_PHRASE}
                  className={`w-full px-3 py-2 border-2 rounded-md focus:ring-2 text-sm font-mono ${
                    phraseValid
                      ? 'border-red-400 focus:ring-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-gray-500'
                  }`}
                  disabled={running}
                  data-testid="factory-reset-phrase-input"
                  autoComplete="off"
                />
              </div>

              {running && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                  <span className="text-sm text-blue-800">
                    جارٍ المسح وإعادة التهيئة… لا تغلق النافذة.
                  </span>
                </div>
              )}
            </>
          )}

          {result && (
            <div className="p-4 bg-green-50 border border-green-300 rounded-lg space-y-3"
                 data-testid="factory-reset-result">
              <div className="flex items-center gap-2 text-green-800 font-semibold">
                <CheckCircle2 className="w-5 h-5" />
                اكتمل المسح · تم إعادة التهيئة
              </div>
              <div className="text-sm space-y-1">
                <div>مجموعات محذوفة: <strong>{result.collections_dropped}</strong></div>
                <div>الشركة الافتراضية: <code>{result.seeded?.company?.code}</code></div>
                <div>حساب المسؤول: <code>{result.default_admin_email}</code></div>
                <div>كلمة المرور: <code>{result.default_admin_password}</code></div>
              </div>
              <p className="text-xs text-gray-700 bg-yellow-50 p-2 rounded border border-yellow-200">
                ⚠️ <strong>سجّل الخروج فوراً</strong> ثم الدخول بالبيانات الافتراضية أعلاه.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button
            onClick={handleClose}
            disabled={running}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50"
            data-testid="factory-reset-cancel-btn"
          >
            {result ? 'إغلاق' : 'إلغاء'}
          </button>
          {!result && (
            <button
              onClick={runReset}
              disabled={running || !phraseValid}
              className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-md disabled:opacity-40 flex items-center gap-2"
              data-testid="factory-reset-run-btn"
            >
              {running
                ? <><Loader2 className="w-4 h-4 animate-spin" /> جارٍ المسح…</>
                : <><Trash2 className="w-4 h-4" /> امسح كل شيء الآن</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FactoryResetModal;
